'use client';

import { useState } from 'react';

import { SubscriptionLevelBadge } from '@ps/features/subscription/components/SubscriptionLevelBadge';
import { PopStoreExtendedSubscriptionLevel } from '@ps/features/subscription/types/SubscriptionLevel';
import { DialogTitle } from '@ps/ui/atoms/dialog';
import { Bookmark, LogOut, Users } from 'lucide-react';

import { useLeaveChannel } from '../../hooks/useLeaveChannel';
import { useMyChannelBookmarks } from '../../hooks/useMyChannelBookmarks';
import { useMyChannelMembership } from '../../hooks/useMyChannelMembership';
import { ChannelVisibility } from '../../types/ChannelVisibility';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelAvatar } from '../common/ChannelAvatar';
import { CommunityModal } from '../common/CommunityModal';
import { ChannelBookmarksSheet } from './ChannelBookmarksSheet';
import {
  ChannelInfoRow,
  ChannelInfoRowButton,
  RowCount,
} from './ChannelInfoRows';
import { ChannelMembersSheet } from './ChannelMembersSheet';
import { ChannelNotificationsControl } from './ChannelNotificationsControl';
import { LeaveChannelDialog } from './LeaveChannelDialog';

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

type OpenSheet = 'members' | 'bookmarks' | null;

/**
 * Channel info a NON-owner sees from the header menu's "Channel Info". A
 * non-member sees the read-only basics (type + required subscription); once
 * they've joined, the member rows appear — Members / Bookmarks / Notifications
 * — mirroring the owner {@link ChannelInfoPopover} minus the owner-only Reports
 * / Edit / Delete. Sub-panes (Members / Bookmarks) open as sibling sheets; the
 * dialog dismisses while one is showing (same pattern as the popover).
 */
export function ChannelInfoDialog({ channel, open, onOpenChange }: Props) {
  const member = useMyChannelMembership(channel.id);
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const { leave } = useLeaveChannel(channel);

  // Only subscribe while open; the hook bails on a null channelId.
  const bookmarks = useMyChannelBookmarks(open ? channel.id : undefined);
  const bookmarkCount = bookmarks.bookmarks.length;

  const isPrivate = channel.visibility === ChannelVisibility.Private;
  // A membership doc present = joined. `undefined` = still resolving, `null` =
  // confirmed not a member — both hide the member-only rows.
  const isMember = member != null;

  const openSubPanel = (panel: Exclude<OpenSheet, null>) => {
    onOpenChange(false);
    setOpenSheet(panel);
  };

  return (
    <>
      <CommunityModal open={open} onOpenChange={onOpenChange}>
        <div className="flex flex-col items-center gap-2 pt-2">
          <ChannelAvatar src={channel.icon} name={channel.name} size={64} />
          <DialogTitle className="text-content-1 text-center text-lg font-semibold">
            {channel.name}
          </DialogTitle>
          <p className="text-content-3 text-sm">
            {channel.messageCount} Posts · {channel.memberCount} Members
          </p>
          {channel.description ? (
            <p className="text-content-2 text-center text-sm">
              {channel.description}
            </p>
          ) : null}
        </div>

        <div className="divide-line border-line mt-2 divide-y rounded-md border">
          {isMember ? (
            <>
              <ChannelInfoRowButton
                icon={<Users className="h-4 w-4" aria-hidden />}
                label="Members"
                right={<RowCount value={channel.memberCount} />}
                onClick={() => openSubPanel('members')}
              />
              <ChannelInfoRowButton
                icon={<Bookmark className="h-4 w-4" aria-hidden />}
                label="Bookmarks"
                right={<RowCount value={bookmarkCount} />}
                onClick={() => openSubPanel('bookmarks')}
              />
              <ChannelNotificationsControl
                channelId={channel.id}
                member={member}
                includeCreatorMessages
              />
            </>
          ) : null}
          <ChannelInfoRow
            label="Channel Type"
            right={
              <span className="text-content-1 text-sm">
                {isPrivate ? 'Private' : 'Public'}
              </span>
            }
          />
          <ChannelInfoRow
            label="Required Subscription"
            right={
              channel.minimumSubscriptionLevel === 'follower' ? (
                <span className="text-content-2 text-sm">Follower</span>
              ) : (
                <SubscriptionLevelBadge
                  level={
                    channel.minimumSubscriptionLevel as PopStoreExtendedSubscriptionLevel
                  }
                />
              )
            }
          />
        </div>

        {isMember ? (
          <button
            type="button"
            onClick={() => setConfirmingLeave(true)}
            className="mt-1 flex items-center justify-center gap-2 py-1 text-sm font-semibold text-red-400 transition-colors hover:text-red-300"
          >
            <LogOut className="h-4 w-4" aria-hidden /> Leave Channel
          </button>
        ) : null}
      </CommunityModal>

      <LeaveChannelDialog
        open={confirmingLeave}
        onOpenChange={setConfirmingLeave}
        onConfirm={async () => {
          await leave();
          // The membership doc subscription flips this surface back to the
          // non-member gate; close the info dialog on success.
          onOpenChange(false);
        }}
      />

      <ChannelMembersSheet
        channel={channel}
        open={openSheet === 'members'}
        onOpenChange={next => setOpenSheet(next ? 'members' : null)}
      />
      <ChannelBookmarksSheet
        channel={channel}
        open={openSheet === 'bookmarks'}
        onOpenChange={next => setOpenSheet(next ? 'bookmarks' : null)}
      />
    </>
  );
}
